import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { map, merge, Subscription, take } from 'rxjs';
import type { Observable } from 'rxjs';

export type TableStartupFirstMessageType =
    | 'definition'
    | 'descriptor'
    | 'end-of-data'
    | 'progress'
    | 'row-count'
    | 'totals-descriptor'
    | 'totals-update'
    | 'updates';

export interface TableStartupTiming {
    readonly queryFnStartedAt: number | undefined;
    readonly queryFnCompletedAt: number | undefined;
    readonly tableRequestStartedAt: number | undefined;
    readonly tableRequestCompletedAt: number | undefined;
    readonly tableKnownAt: number;
    readonly tableClientCreationStartedAt: number;
    readonly tableClientConstructedAt: number;
    readonly tableClientConfiguredAt: number;
}

export interface TableStartupContext {
    readonly debugIdentifier: string | undefined;
    readonly proxyGeneration: number;
    readonly proxyKind: 'legacy' | 'nextgen';
    readonly tableKey: string;
    readonly tableModelId: string;
    readonly tableStartupId: string;
}

export interface TableStartupLatencyOptions {
    readonly now?: () => number;
}

interface TableStartupMessageSource {
    readonly dataRequest$: Observable<void>;
    readonly definition$: Observable<unknown>;
    readonly descriptor$: Observable<unknown>;
    readonly endOfData$: Observable<unknown>;
    readonly progressUpdate$: Observable<unknown>;
    readonly rowCount$: Observable<unknown>;
    readonly totalsDescriptor$: Observable<unknown>;
    readonly totalsUpdate$: Observable<unknown>;
    readonly updates$: Observable<unknown>;
}

const durationMs = (startedAt: number, completedAt: number): number =>
    Math.max(0, Math.round((completedAt - startedAt) * 1_000) / 1_000);

const asMessageType = <T>(source: Observable<T>, type: TableStartupFirstMessageType) => source.pipe(map(() => type));

/**
 * Records the client-side table startup handoffs as one correlated Datadog event stream. The
 * TableModel supplies the exact query/model/client timestamps; this observer supplies the exact
 * subscription and first table-message timestamps exposed by TableClient.
 */
export class TableStartupLatency implements Disposable {
    readonly #context: TableStartupContext;
    readonly #log: Logger;
    readonly #now: () => number;
    readonly #subscriptions = new Subscription();
    readonly #timing: TableStartupTiming;
    #firstMessage: { at: number; type: TableStartupFirstMessageType } | undefined;
    #firstMessageLogged = false;
    #subscribedAt: number | undefined;

    constructor(
        log: Logger,
        context: TableStartupContext,
        timing: TableStartupTiming,
        options: TableStartupLatencyOptions = {},
    ) {
        this.#context = context;
        this.#log = log;
        this.#now = options.now ?? (() => performance.now());
        this.#timing = timing;

        this.#log.info({
            ...this.#baseLogData(),
            message: 'Table creation result accepted by TableModel',
            phase: 'table-known',
        });
    }

    observe(tableClient: TableStartupMessageSource): void {
        // TableClient emits dataRequest$ synchronously just after RawClient.subscribe() and the
        // initial snapshot/diff request. Observing before assigning the key captures connected
        // clients exactly; disconnected clients are captured when their transport later connects.
        this.#subscriptions.add(
            tableClient.dataRequest$.pipe(take(1)).subscribe(() => {
                this.markSubscribed();
            }),
        );

        this.#subscriptions.add(
            merge(
                asMessageType(tableClient.definition$, 'definition'),
                asMessageType(tableClient.descriptor$, 'descriptor'),
                asMessageType(tableClient.endOfData$, 'end-of-data'),
                asMessageType(tableClient.progressUpdate$, 'progress'),
                asMessageType(tableClient.rowCount$, 'row-count'),
                asMessageType(tableClient.totalsDescriptor$, 'totals-descriptor'),
                asMessageType(tableClient.totalsUpdate$, 'totals-update'),
                asMessageType(tableClient.updates$, 'updates'),
            )
                .pipe(take(1))
                .subscribe((type) => {
                    this.#firstMessage = { at: this.#now(), type };
                    this.#logFirstMessageIfReady();
                }),
        );
    }

    /**
     * Fallback for subscriptions that do not prime data (for example a zero-sized bounds window).
     * The ordinary positive-window path is captured slightly earlier by dataRequest$.
     */
    markSubscribed(at = this.#now()): void {
        if (this.#subscribedAt !== undefined) {
            return;
        }

        this.#subscribedAt = at;
        this.#log.info({
            ...this.#baseLogData(),
            ...this.#subscriptionLogData(),
            message: 'Table proxy subscription started',
            phase: 'subscription-started',
        });
        this.#logFirstMessageIfReady();
    }

    #baseLogData() {
        const { queryFnStartedAt, queryFnCompletedAt, tableRequestStartedAt, tableRequestCompletedAt, tableKnownAt } =
            this.#timing;
        return {
            event: 'table-startup-latency',
            ...this.#context,
            ...(queryFnStartedAt === undefined || queryFnCompletedAt === undefined
                ? {}
                : {
                      queryFnDurationMs: durationMs(queryFnStartedAt, queryFnCompletedAt),
                      queryFnCompletedToTableKnownMs: durationMs(queryFnCompletedAt, tableKnownAt),
                      queryFnStartToTableKnownMs: durationMs(queryFnStartedAt, tableKnownAt),
                  }),
            ...(queryFnStartedAt === undefined ||
            queryFnCompletedAt === undefined ||
            tableRequestStartedAt === undefined ||
            tableRequestCompletedAt === undefined
                ? {}
                : {
                      queryFnStartToTableRequestStartMs: durationMs(queryFnStartedAt, tableRequestStartedAt),
                      tableRequestDurationMs: durationMs(tableRequestStartedAt, tableRequestCompletedAt),
                      tableRequestCompletedToQueryFnCompletedMs: durationMs(
                          tableRequestCompletedAt,
                          queryFnCompletedAt,
                      ),
                      tableRequestCompletedToTableKnownMs: durationMs(tableRequestCompletedAt, tableKnownAt),
                  }),
        };
    }

    #subscriptionLogData() {
        const subscribedAt = this.#subscribedAt;
        if (subscribedAt === undefined) {
            return {};
        }

        const {
            queryFnStartedAt,
            tableKnownAt,
            tableClientCreationStartedAt,
            tableClientConstructedAt,
            tableClientConfiguredAt,
        } = this.#timing;
        return {
            tableKnownToClientCreationStartedMs: durationMs(tableKnownAt, tableClientCreationStartedAt),
            tableClientConstructionMs: durationMs(tableClientCreationStartedAt, tableClientConstructedAt),
            tableClientConfigurationMs: durationMs(tableClientConstructedAt, tableClientConfiguredAt),
            tableClientConfiguredToSubscriptionMs: durationMs(tableClientConfiguredAt, subscribedAt),
            tableKnownToSubscriptionMs: durationMs(tableKnownAt, subscribedAt),
            ...(queryFnStartedAt === undefined
                ? {}
                : { queryFnStartToSubscriptionMs: durationMs(queryFnStartedAt, subscribedAt) }),
        };
    }

    #logFirstMessageIfReady(): void {
        if (this.#firstMessageLogged || this.#subscribedAt === undefined || this.#firstMessage === undefined) {
            return;
        }

        this.#firstMessageLogged = true;
        const { queryFnStartedAt, tableKnownAt } = this.#timing;
        this.#log.info({
            ...this.#baseLogData(),
            ...this.#subscriptionLogData(),
            message: 'Table received first message',
            phase: 'first-message',
            firstMessageType: this.#firstMessage.type,
            subscriptionToFirstMessageMs: durationMs(this.#subscribedAt, this.#firstMessage.at),
            tableKnownToFirstMessageMs: durationMs(tableKnownAt, this.#firstMessage.at),
            ...(queryFnStartedAt === undefined
                ? {}
                : { queryFnStartToFirstMessageMs: durationMs(queryFnStartedAt, this.#firstMessage.at) }),
        });
    }

    [Symbol.dispose](): void {
        this.#subscriptions.unsubscribe();
    }
}
